import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Container } from "reactstrap";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import { DISPENSING_LOG_PATHS } from "Routes/paths";
import { BreadcrumbContext } from "Layouts/Header";
import { useGlobalStudy } from "context/StudyContext";
import { useDispensingLog } from "../api/useDispensingLog";
import GlobalStudyMismatchWarning from "../../../../Components/Common/GlobalStudyMismatchWarning";
import FullPageLoader from "Components/Common/FullPageLoader";
import DispensingLogStepper from "../components/DispensingLogStepper";
import AddContainerSelection from "../components/AddContainerSelection";
import AddDispensingDetails from "../components/AddDispensingDetails";
import { Step1SummaryData } from "../helpers/dispensing.types";

const AddDispensingLog: React.FC = () => {
  /* ------------------------------------------------------------------------ */
  /* 1. ROUTING, CONTEXT & GLOBAL STUDY                                       */
  /* ------------------------------------------------------------------------ */
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const [searchParams] = useSearchParams();
  const editId = paramId || searchParams.get("id");

  const breadcrumbContext = useContext(BreadcrumbContext);
  const { selectedStudy: globalStudy } = useGlobalStudy();

  /* ------------------------------------------------------------------------ */
  /* 2. LOCAL STATE & REFS                                                    */
  /* ------------------------------------------------------------------------ */
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isStep1Saved, setIsStep1Saved] = useState<boolean>(Boolean(editId));
  const [step1Summary, setStep1Summary] = useState<Step1SummaryData | null>(null);

  /* ------------------------------------------------------------------------ */
  /* 3. API & SERVICES QUERIES                                                */
  /* ------------------------------------------------------------------------ */
  const { DropdownQuery, SelectQuery } = useDispensingLog({
    getStudyDropDown: true,
    dispensingid: editId ? Number(editId) : null,
  });

  /* ------------------------------------------------------------------------ */
  /* 4. useMemo                                                             */
  /* ------------------------------------------------------------------------ */
  const selectedRecord = useMemo(() => {
    const rawData = SelectQuery?.data?.data;
    if (Array.isArray(rawData) && rawData.length > 0) return rawData[0];
    return rawData || null;
  }, [SelectQuery?.data]);

  const studyOptions = useMemo(
    () =>
      (DropdownQuery.data as any)?.data?.map((study: any) => ({
        label: study.studycode,
        value: String(study.studyid),
        subjectno: study.subjectno || [],
      })) || [],
    [DropdownQuery.data]
  );

  /* ------------------------------------------------------------------------ */
  /* 5. EFFECTS (useEffect) & BREADCRUMBS                                     */
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    document.title = editId ? "Edit Dispensing Log" : "Add Dispensing Log";
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Dispensing Log", link: DISPENSING_LOG_PATHS.LIST },
      { text: editId ? "Edit Dispensing Log" : "Add Dispensing Log" },
    ]);
    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, [editId]);

  /* ------------------------------------------------------------------------ */
  /* 6. EVENT HANDLERS & STEP NAVIGATION                                      */
  /* ------------------------------------------------------------------------ */
  const handleStep1Success = (summary: Step1SummaryData) => {
    setStep1Summary(summary);
    setIsStep1Saved(true);
    setCurrentStep(2);
  };

  /* ------------------------------------------------------------------------ */
  /* 7. RENDER COMPONENT                                                      */
  /* ------------------------------------------------------------------------ */
  if (editId && (SelectQuery.isLoading || SelectQuery.isFetching)) {
    return (
      <Container fluid className={`px-0 ${formStyles.pageContainer}`}>
        <FullPageLoader />
      </Container>
    );
  }

  return (
    <div className={`page-content ${formStyles.pageContainer}`}>
      <Container fluid>
        {/* Global Study Mismatch Banner */}
        <GlobalStudyMismatchWarning
          globalStudy={globalStudy}
          studyOptions={studyOptions}
          isLoaded={!DropdownQuery.isLoading}
        />

        {/* Stepper Navigation */}
        <DispensingLogStepper
          currentStep={currentStep}
          onStepChange={(step) => setCurrentStep(step)}
          isStep2Enabled={isStep1Saved || Boolean(editId)}
        />

        {/* Step 1: Container Selection */}
        {currentStep === 1 && (
          <AddContainerSelection
            editId={editId}
            selectedRecord={selectedRecord}
            isStep1Saved={isStep1Saved}
            setIsStep1Saved={setIsStep1Saved}
            onStepChange={(step) => setCurrentStep(step)}
            onSuccessSubmit={handleStep1Success}
            onCancel={() => navigate(DISPENSING_LOG_PATHS.LIST)}
          />
        )}

        {/* Step 2: Dispensing Dosing Details */}
        {currentStep === 2 && (
          <AddDispensingDetails
            step1Summary={step1Summary}
            editId={editId}
            selectedRecord={selectedRecord}
            onPrevious={() => setCurrentStep(1)}
            onCancel={() => navigate(DISPENSING_LOG_PATHS.LIST)}
          />
        )}
      </Container>
    </div>
  );
};

export default AddDispensingLog;
